"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

import InfoCircleIcon from "@repo/ui-system/icons/buttons/info/circle-outline.svg";

import { env } from "@/lib/env/client";

declare global {
  interface Window {
    Cookiebot?: { renew: () => void };
  }
}

/**
 * Returns true once any pixel of the <footer> element enters the viewport.
 * Uses IntersectionObserver — no scroll listener, fires at most once per
 * enter/leave transition.
 */
function useFooterVisible(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  return visible;
}

// ─── Fixed variants (live in root layout) ────────────────────────────────────

function UsBarFixed({ hidden }: { hidden: boolean }) {
  if (hidden) return null;
  return (
    <button
      type="button"
      onClick={() => window.Cookiebot?.renew()}
      className="fixed bottom-0 left-0 right-0 z-50 w-full flex items-center justify-center gap-2 py-2 cursor-pointer"
      style={{ background: "#cae1fc" }}
      aria-label="Do not sell or share my personal data — open cookie preferences"
    >
      <InfoCircleIcon
        aria-hidden="true"
        className="size-4 text-[#64697d]"
      />
      <span
        className="text-[14px] leading-5 font-normal underline"
        style={{ color: "#64697d" }}
      >
        Do not sell or share my personal data
      </span>
    </button>
  );
}

function AuBarFixed({ hidden }: { hidden: boolean }) {
  if (hidden) return null;
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 w-full flex items-center justify-center gap-2 py-2"
      style={{ background: "#cae1fc" }}
    >
      <InfoCircleIcon
        aria-hidden="true"
        className="size-4 text-[#64697d]"
      />
      <span
        className="text-[14px] leading-5 font-normal"
        style={{ color: "#64697d" }}
      >
        About this content
        {" | "}
        <Link
          href="/pages/privacy-policy"
          className="underline"
        >
          Privacy Policy
        </Link>
      </span>
    </div>
  );
}

// ─── Inline variants (live as first child of <footer>) ───────────────────────

function UsBarInline() {
  return (
    <button
      type="button"
      onClick={() => window.Cookiebot?.renew()}
      className="flex items-center gap-2 cursor-pointer"
      aria-label="Do not sell or share my personal data — open cookie preferences"
    >
      <InfoCircleIcon
        aria-hidden="true"
        className="size-4 text-[#64697d]"
      />
      <span
        className="text-[14px] leading-5 font-normal underline"
        style={{ color: "#64697d" }}
      >
        Do not sell or share my personal data
      </span>
    </button>
  );
}

function AuBarInline() {
  return (
    <div className="w-full flex items-center justify-center gap-2 py-2">
      <InfoCircleIcon
        aria-hidden="true"
        className="size-4 text-[#64697d]"
      />
      <span
        className="text-[14px] leading-5 font-normal"
        style={{ color: "#64697d" }}
      >
        About this content
        {" | "}
        <Link
          href="/pages/privacy-policy"
          className="underline"
        >
          Privacy Policy
        </Link>
      </span>
    </div>
  );
}

// ─── Public exports ───────────────────────────────────────────────────────────

/**
 * Fixed consent bar — NOT currently mounted (UX request: footer-only rendering).
 * To restore the sticky viewport bar, add <ConsentBarFixed /> back to
 * app/layout.tsx alongside <ConsentBarFooter /> in the LPV footer.
 *
 * When mounted: sticks to viewport bottom while the footer is off-screen,
 * then hides itself (via IntersectionObserver) once the footer enters the
 * viewport, handing off to ConsentBarFooter.
 *
 * Consent modes:
 * - "none-with-footer-link" (US/CA): CCPA "Do not sell" bar → opens Cookiebot modal
 * - "none" (AU): Privacy info bar → links to /privacy-policy
 * - "own-cookiebot" (UK): returns null
 */
export function ConsentBarFixed() {
  const mode = env.NEXT_PUBLIC_CONSENT_MODE;
  const footerVisible = useFooterVisible();

  if (mode === "none-with-footer-link") {
    return <UsBarFixed hidden={footerVisible} />;
  }

  if (mode === "none") {
    return <AuBarFixed hidden={footerVisible} />;
  }

  return null;
}

/**
 * Inline consent bar — place as the first child of <footer>.
 * Renders at the natural top of the footer with no positioning.
 * Appears at the exact scroll position where ConsentBarFixed disappears,
 * creating a seamless positional handoff.
 */
export function ConsentBarFooter() {
  const mode = env.NEXT_PUBLIC_CONSENT_MODE;

  if (mode === "none-with-footer-link") {
    return <UsBarInline />;
  }

  if (mode === "none") {
    return <AuBarInline />;
  }

  return null;
}
